import React, { useState, useEffect } from 'react';
import './AlbumMappingTool.css';
import { API_BASE, getAuthHeaders } from '../config/api';

function AlbumMappingTool({ user }) {
  const [jbxxAlbums, setJbxxAlbums] = useState([]);
  const [allJbxxAlbums, setAllJbxxAlbums] = useState([]); // All albums for filter dropdowns
  const [mbReleases, setMbReleases] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState('');
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState({});
  const [filters, setFilters] = useState({
    type: '',
    language: 'all',
    genre: 'all',
    subgenre: 'all',
    artist: 'all',
    album: 'all',
  });
  const [filtersReady, setFiltersReady] = useState({
    language: false,
    genre: false,
    subgenre: false,
    artist: false,
    album: false,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJBXX, setSelectedJBXX] = useState(null);
  const [selectedMB, setSelectedMB] = useState(null);

  // Load JBXX albums
  useEffect(() => {
    loadJBXXAlbums();
    loadMappings();
  }, [filters]);

  // Load unique artists from filtered albums
  useEffect(() => {
    const uniqueArtists = new Set();
    jbxxAlbums.forEach((album) => {
      const artist = album.artist || album.artistName;
      if (artist) {
        uniqueArtists.add(artist);
      }
    });
    setArtists(Array.from(uniqueArtists).sort());
  }, [jbxxAlbums]);

  // Get unique values for filter dropdowns (similar to Upload Manager)
  const getUniqueValues = (field) => {
    const values = new Set();
    // Use allJbxxAlbums for filter dropdowns
    let filtered = allJbxxAlbums.length > 0 ? allJbxxAlbums : jbxxAlbums;

    // Apply filters (skip the field we're getting unique values for)
    if (filters.type !== 'all' && field !== 'type') {
      filtered = filtered.filter((album) => {
        const albumType = (album.type || '').toString().trim();
        const filterType = filters.type.toString().trim();
        return albumType.toLowerCase() === filterType.toLowerCase();
      });
    }
    if (filters.language !== 'all' && field !== 'language') {
      filtered = filtered.filter((album) => {
        const albumLang = (album.language || '').toString().trim();
        const filterLang = filters.language.toString().trim();
        return albumLang.toLowerCase() === filterLang.toLowerCase();
      });
    }
    if (filters.genre !== 'all' && field !== 'genre') {
      filtered = filtered.filter((album) => {
        const albumGenre = (album.genre || '').toString().trim();
        const filterGenre = filters.genre.toString().trim();
        return albumGenre.toLowerCase() === filterGenre.toLowerCase();
      });
    }
    if (filters.subgenre !== 'all' && field !== 'subgenre') {
      filtered = filtered.filter((album) => {
        const albumSubgenre = (album.subgenre || '').toString().trim();
        const filterSubgenre = filters.subgenre.toString().trim();
        return albumSubgenre.toLowerCase() === filterSubgenre.toLowerCase();
      });
    }
    if (filters.artist !== 'all' && field !== 'artist') {
      filtered = filtered.filter((album) => {
        const albumArtist = (album.artist || album.artistName || '')
          .toString()
          .trim();
        const filterArtist = filters.artist.toString().trim();
        return albumArtist.toLowerCase() === filterArtist.toLowerCase();
      });
    }
    if (filters.album !== 'all' && field !== 'album') {
      filtered = filtered.filter((album) => {
        const albumName = (album.album || album.albumName || '')
          .toString()
          .trim();
        const filterAlbum = filters.album.toString().trim();
        return albumName.toLowerCase() === filterAlbum.toLowerCase();
      });
    }

    // Extract unique values for the requested field
    filtered.forEach((album) => {
      let value = album[field];
      if (!value && field === 'album') {
        value = album.albumName;
      }
      if (!value && field === 'artist') {
        value = album.artistName;
      }
      if (
        value &&
        typeof value === 'string' &&
        value !== 'all' &&
        value.trim() !== ''
      ) {
        values.add(value);
      } else if (value && typeof value !== 'string') {
        values.add(String(value));
      }
    });

    return Array.from(values).sort();
  };

  const loadJBXXAlbums = async () => {
    setLoading(true);
    try {
      // Require type to be selected before loading
      if (!filters.type) {
        setJbxxAlbums([]);
        setAllJbxxAlbums([]);
        setFiltersReady({
          language: false,
          genre: false,
          subgenre: false,
          artist: false,
          album: false,
        });
        setLoading(false);
        return;
      }

      // Build query params - only send type and language to API (API supports these)
      const params = new URLSearchParams({
        limit: '2000',
      });
      params.append('type', filters.type);
      if (filters.language && filters.language !== 'all') {
        params.append('language', filters.language);
      }

      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/jbxx-albums?${params}`, {
        headers,
      });
      const data = await response.json();
      const albums = data.albums || [];

      // Store all albums for filter dropdowns
      setAllJbxxAlbums(albums);

      // Apply client-side filtering for genre, subgenre, artist, album
      // (type and language are already filtered by the API)
      const filtered = albums.filter((album) => {
        if (filters.genre !== 'all') {
          const albumGenre = (album.genre || '').toString().trim();
          const filterGenre = filters.genre.toString().trim();
          if (albumGenre.toLowerCase() !== filterGenre.toLowerCase()) {
            return false;
          }
        }
        if (filters.subgenre !== 'all') {
          const albumSubgenre = (album.subgenre || '').toString().trim();
          const filterSubgenre = filters.subgenre.toString().trim();
          if (albumSubgenre.toLowerCase() !== filterSubgenre.toLowerCase()) {
            return false;
          }
        }
        if (filters.artist !== 'all') {
          const albumArtist = (album.artist || album.artistName || '')
            .toString()
            .trim();
          const filterArtist = filters.artist.toString().trim();
          if (albumArtist.toLowerCase() !== filterArtist.toLowerCase()) {
            return false;
          }
        }
        if (filters.album !== 'all') {
          const albumName = (album.album || album.albumName || '')
            .toString()
            .trim();
          const filterAlbum = filters.album.toString().trim();
          if (albumName.toLowerCase() !== filterAlbum.toLowerCase()) {
            return false;
          }
        }
        return true;
      });

      setJbxxAlbums(filtered);

      // Update filter readiness - enable next filter only after data is loaded
      // Language is ready when type is selected and albums are loaded
      // Genre is ready when language is selected and albums are loaded
      // And so on...
      setFiltersReady({
        language: filters.type !== '' && albums.length > 0,
        genre:
          filters.type !== '' &&
          filters.language !== 'all' &&
          albums.length > 0,
        subgenre:
          filters.type !== '' &&
          filters.language !== 'all' &&
          filters.genre !== 'all' &&
          albums.length > 0,
        artist:
          filters.type !== '' &&
          filters.language !== 'all' &&
          filters.genre !== 'all' &&
          filters.subgenre !== 'all' &&
          albums.length > 0,
        album:
          filters.type !== '' &&
          filters.language !== 'all' &&
          filters.genre !== 'all' &&
          filters.subgenre !== 'all' &&
          filters.artist !== 'all' &&
          albums.length > 0,
      });
    } catch (error) {
      console.error('Error loading JBXX albums:', error);
      alert(
        'Failed to load JBXX albums. Make sure the metadata server is running.'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadArtists = async () => {
    try {
      // Use filtered albums to populate artist dropdown
      const uniqueArtists = new Set();
      jbxxAlbums.forEach((album) => {
        const artist = album.artist || album.artistName;
        if (artist) {
          uniqueArtists.add(artist);
        }
      });
      setArtists(Array.from(uniqueArtists).sort());
    } catch (error) {
      console.error('Error loading artists:', error);
    }
  };

  const loadMusicBrainzReleases = async (artistName) => {
    if (!artistName) return;

    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/mb-artist-releases`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ artistName, limit: 500 }),
      });

      const data = await response.json();
      setMbReleases(data.releases || []);
    } catch (error) {
      console.error('Error loading MusicBrainz releases:', error);
      alert(
        'Failed to load MusicBrainz releases. Make sure the metadata server is running.'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadMappings = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/mappings`, { headers });
      const data = await response.json();
      const mappingMap = {};
      Object.values(data.mappings || {}).forEach((mapping) => {
        mappingMap[mapping.jbxxId] = mapping;
      });
      setMappings(mappingMap);
    } catch (error) {
      console.error('Error loading mappings:', error);
    }
  };

  const handleArtistSelect = (artist) => {
    setSelectedArtist(artist);
    loadMusicBrainzReleases(artist);
  };

  const handleJBXXClick = (album) => {
    if (selectedJBXX?.id === album.id) {
      setSelectedJBXX(null);
    } else {
      setSelectedJBXX(album);
      setSelectedMB(null);
    }
  };

  const handleMBClick = (release) => {
    if (selectedMB?.mbid === release.mbid) {
      setSelectedMB(null);
    } else {
      setSelectedMB(release);
      // If JBXX is selected, auto-map
      if (selectedJBXX) {
        handleMap(selectedJBXX, release);
      }
    }
  };

  const handleMap = async (jbxxAlbum, mbRelease) => {
    if (!window.confirm(`Map "${jbxxAlbum.album}" to "${mbRelease.album}"?`)) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/save-mapping`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          jbxxId: jbxxAlbum.id,
          mbMbid: mbRelease.mbid,
          mbAlbum: mbRelease.album,
          mbArtist: mbRelease.artist,
          mbYear: mbRelease.year,
          mappedBy: user?.email || 'unknown',
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Update local state
        setMappings((prev) => ({
          ...prev,
          [jbxxAlbum.id]: data.mapping,
        }));
        setSelectedJBXX(null);
        setSelectedMB(null);
        alert('✅ Mapping saved successfully!');
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
      alert('Failed to save mapping.');
    }
  };

  const handleUnmap = async (jbxxId) => {
    if (!window.confirm('Remove this mapping?')) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/mapping/${jbxxId}`, {
        method: 'DELETE',
        headers,
      });

      if (response.ok) {
        const newMappings = { ...mappings };
        delete newMappings[jbxxId];
        setMappings(newMappings);
        alert('✅ Mapping removed!');
      }
    } catch (error) {
      console.error('Error removing mapping:', error);
      alert('Failed to remove mapping.');
    }
  };

  // Filter JBXX albums by search term
  const filteredJBXXAlbums = jbxxAlbums.filter((album) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (album.album || '').toLowerCase().includes(search) ||
      (album.artist || '').toLowerCase().includes(search)
    );
  });

  // Filter JBXX albums by selected artist (for MusicBrainz lookup)
  const artistJBXXAlbums = selectedArtist
    ? filteredJBXXAlbums.filter((album) => album.artist === selectedArtist)
    : filteredJBXXAlbums;

  return (
    <div className="mapping-tool">
      <div className="mapping-tool-header">
        <h1>🎯 Album Mapping Tool</h1>
        <p>Map JBXX albums to MusicBrainz releases</p>
      </div>

      {/* Filters */}
      <div className="mapping-filters">
        <div className="filter-group">
          <label>Type:</label>
          <select
            value={filters.type}
            onChange={(e) => {
              setFilters({
                ...filters,
                type: e.target.value,
                language: 'all',
                genre: 'all',
                subgenre: 'all',
                artist: 'all',
                album: 'all',
              });
              // Reset filter readiness when type changes
              setFiltersReady({
                language: false,
                genre: false,
                subgenre: false,
                artist: false,
                album: false,
              });
            }}
          >
            <option value="">-- Select Type --</option>
            <option value="Albums">Albums</option>
            <option value="Singles">Singles</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Language:</label>
          <select
            value={filters.language}
            disabled={
              !filters.type ||
              (filters.language === 'all' &&
                (!filtersReady.language || loading))
            }
            onChange={(e) => {
              setFilters({
                ...filters,
                language: e.target.value,
                genre: 'all',
                subgenre: 'all',
                artist: 'all',
                album: 'all',
              });
              // Reset downstream filter readiness when language changes
              setFiltersReady((prev) => ({
                ...prev,
                genre: false,
                subgenre: false,
                artist: false,
                album: false,
              }));
            }}
          >
            <option value="all">All</option>
            {filters.type &&
              getUniqueValues('language').map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Genre:</label>
          <select
            value={filters.genre}
            disabled={
              !filters.type ||
              filters.language === 'all' ||
              (filters.genre === 'all' && (!filtersReady.genre || loading))
            }
            onChange={(e) => {
              setFilters({
                ...filters,
                genre: e.target.value,
                subgenre: 'all',
                artist: 'all',
                album: 'all',
              });
              // Reset downstream filter readiness when genre changes
              setFiltersReady((prev) => ({
                ...prev,
                subgenre: false,
                artist: false,
                album: false,
              }));
            }}
          >
            <option value="all">All</option>
            {filters.type &&
              filters.language !== 'all' &&
              getUniqueValues('genre').map((genre) => (
                <option key={genre} value={genre}>
                  {genre}
                </option>
              ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Subgenre:</label>
          <select
            value={filters.subgenre}
            disabled={
              !filters.type ||
              filters.language === 'all' ||
              filters.genre === 'all' ||
              (filters.subgenre === 'all' &&
                (!filtersReady.subgenre || loading))
            }
            onChange={(e) => {
              setFilters({
                ...filters,
                subgenre: e.target.value,
                artist: 'all',
                album: 'all',
              });
              // Reset downstream filter readiness when subgenre changes
              setFiltersReady((prev) => ({
                ...prev,
                artist: false,
                album: false,
              }));
            }}
          >
            <option value="all">All</option>
            {filters.type &&
              filters.language !== 'all' &&
              filters.genre !== 'all' &&
              getUniqueValues('subgenre').map((subgenre) => (
                <option key={subgenre} value={subgenre}>
                  {subgenre}
                </option>
              ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Artist:</label>
          <select
            value={filters.artist}
            disabled={
              !filters.type ||
              filters.language === 'all' ||
              filters.genre === 'all' ||
              filters.subgenre === 'all' ||
              (filters.artist === 'all' && (!filtersReady.artist || loading))
            }
            onChange={(e) => {
              setFilters({ ...filters, artist: e.target.value, album: 'all' });
              // Reset downstream filter readiness when artist changes
              setFiltersReady((prev) => ({
                ...prev,
                album: false,
              }));
            }}
          >
            <option value="all">All</option>
            {filters.type &&
              filters.language !== 'all' &&
              filters.genre !== 'all' &&
              filters.subgenre !== 'all' &&
              getUniqueValues('artist').map((artist) => (
                <option key={artist} value={artist}>
                  {artist}
                </option>
              ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Album:</label>
          <select
            value={filters.album}
            disabled={
              !filters.type ||
              filters.language === 'all' ||
              filters.genre === 'all' ||
              filters.subgenre === 'all' ||
              filters.artist === 'all' ||
              (filters.album === 'all' && (!filtersReady.album || loading))
            }
            onChange={(e) => setFilters({ ...filters, album: e.target.value })}
          >
            <option value="all">All</option>
            {filters.type &&
              filters.language !== 'all' &&
              filters.genre !== 'all' &&
              filters.subgenre !== 'all' &&
              filters.artist !== 'all' &&
              getUniqueValues('album').map((album) => (
                <option key={album} value={album}>
                  {album}
                </option>
              ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search albums..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '8px', width: '200px' }}
          />
        </div>

        <div className="filter-group">
          <label style={{ visibility: 'hidden' }}>Refresh:</label>
          <button onClick={loadJBXXAlbums} disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Artist Selector */}
      <div className="artist-selector">
        <label>Select Artist:</label>
        <select
          value={selectedArtist}
          onChange={(e) => handleArtistSelect(e.target.value)}
          style={{ padding: '8px', minWidth: '300px' }}
        >
          <option value="">-- Select an artist --</option>
          {artists.map((artist) => (
            <option key={artist} value={artist}>
              {artist}
            </option>
          ))}
        </select>
        {selectedArtist && (
          <span className="mb-count">
            {mbReleases.length} MusicBrainz releases found
          </span>
        )}
      </div>

      {/* Main Content */}
      <div className="mapping-content">
        {/* Left: JBXX Albums */}
        <div className="mapping-panel jbxx-panel">
          <h2>
            📚 JBXX Albums ({artistJBXXAlbums.length})
            {selectedArtist && ` - ${selectedArtist}`}
            {loading && (
              <span className="mapping-loading-indicator">
                <span className="mapping-loading-text">
                  Loading library ...
                </span>
                <span className="mapping-loading-spinner">⏳</span>
              </span>
            )}
          </h2>
          <div className="album-list">
            {loading ? (
              <div className="loading">Loading...</div>
            ) : artistJBXXAlbums.length === 0 ? (
              <div className="empty-state">
                {!filters.type
                  ? 'Please select a Type to begin'
                  : selectedArtist
                  ? `No albums found for "${selectedArtist}"`
                  : 'No albums match the current filters'}
              </div>
            ) : (
              artistJBXXAlbums.map((album) => {
                const mapping = mappings[album.id];
                const isSelected = selectedJBXX?.id === album.id;
                return (
                  <div
                    key={album.id}
                    className={`album-item ${mapping ? 'mapped' : ''} ${
                      isSelected ? 'selected' : ''
                    }`}
                    onClick={() => handleJBXXClick(album)}
                  >
                    <div className="album-info">
                      <div className="album-title">{album.album}</div>
                      <div className="album-meta">
                        {album.artist} • {album.type} • {album.language}
                        {album.year && ` • ${album.year}`}
                        {album.songsCount && ` • ${album.songsCount} tracks`}
                      </div>
                      {mapping && (
                        <div className="mapping-badge">
                          ✅ Mapped to: {mapping.mbAlbum}
                        </div>
                      )}
                    </div>
                    {mapping && (
                      <button
                        className="btn-unmap"
                        onClick={() => handleUnmap(album.id)}
                        title="Remove mapping"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right: MusicBrainz Releases */}
        <div className="mapping-panel mb-panel">
          <h2>
            🎵 MusicBrainz Releases ({mbReleases.length})
            {selectedArtist && ` - ${selectedArtist}`}
          </h2>
          <div className="album-list">
            {loading ? (
              <div className="loading">Loading...</div>
            ) : mbReleases.length === 0 ? (
              <div className="empty-state">
                {selectedArtist
                  ? `No releases found for "${selectedArtist}"`
                  : 'Select an artist to view releases'}
              </div>
            ) : (
              mbReleases.map((release) => {
                const isSelected = selectedMB?.mbid === release.mbid;
                return (
                  <div
                    key={release.mbid}
                    className={`album-item mb-item ${
                      isSelected ? 'selected' : ''
                    }`}
                    onClick={() => handleMBClick(release)}
                  >
                    <div className="album-info">
                      <div className="album-title">{release.album}</div>
                      <div className="album-meta">
                        {release.artist} • {release.type || 'Unknown'}
                        {release.year && ` • ${release.year}`}
                        {release.trackCount &&
                          ` • ${release.trackCount} tracks`}
                      </div>
                      <div className="mbid">MBID: {release.mbid}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Mapping Instructions */}
      {!loading && jbxxAlbums.length > 0 && (
        <div className="mapping-instructions">
          <h3>How to Map:</h3>
          <ol>
            <li>Select an artist from the dropdown above</li>
            <li>Find the JBXX album on the left</li>
            <li>Find the matching MusicBrainz release on the right</li>
            <li>
              Click on the JBXX album, then click on the MusicBrainz release to
              map them
            </li>
            <li>Mapped albums will show a green badge</li>
          </ol>
        </div>
      )}
    </div>
  );
}

export default AlbumMappingTool;
